/**
 * Markup de la galería `/debriefings.html` — Fase 7g.2.
 *
 * Estructura idéntica a la de briefings (mismas clases CSS
 * `.bgallery-*` que se reutilizan); cambian solo el título, los
 * textos del botón "Nuevo" y la columna MISIÓN se interpreta como
 * "misión a la que se refiere el debriefing".
 *
 * Botón X de borrado por fila: visible si `canDeleteDebriefing(user, d)`,
 * que solo permite autor + superadmin (NO admin). Decisión Fase 7b
 * actualizada 2026-05-10.
 */

import type {
  AuthenticatedPilot,
  Debriefing,
  MissionSummary,
} from '@51esc/shared';
import { canDeleteDebriefing } from '../auth/permissions.js';
import type { BriefingMission } from '../briefing/api.js';

/**
 * Shell de la galería. `user === null` (anónimo) → solo lectura: se
 * omite el botón "+ Nuevo debriefing", que lleva al editor.
 */
export function renderGalleryShell(user: AuthenticatedPilot | null): string {
  const newBtn =
    user !== null
      ? `<a href="/debriefing.html" class="bgallery-new-btn">+ Nuevo debriefing</a>`
      : '';
  return `
    <div class="bgallery-stage">
      <header class="bgallery-header">
        <h1 class="bgallery-title">DEBRIEFINGS</h1>
        ${newBtn}
      </header>
      <div class="bgallery-table-wrap">
        <table class="bgallery-table">
          <thead>
            <tr>
              <th class="bgallery-col-pilot">PILOTO</th>
              <th class="bgallery-col-date">FECHA MISIÓN</th>
              <th class="bgallery-col-mission">MISIÓN</th>
              <th class="bgallery-col-campaign">CAMPAÑA</th>
              <th class="bgallery-col-photos">FOTOS</th>
              <th class="bgallery-col-vis">VIS</th>
              <th class="bgallery-col-updated">ACTUALIZADO</th>
              <th class="bgallery-col-actions" aria-label="Acciones"></th>
            </tr>
          </thead>
          <tbody data-bgallery-rows>
            <tr><td colspan="8" class="bgallery-loading">cargando…</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  `;
}

/** Una fila. `user === null` (anónimo) → sin botón X de borrado. */
export function renderRow(
  d: Debriefing,
  missionsCache: Map<string, BriefingMission>,
  declaredMissionsCache: Map<string, MissionSummary>,
  user: AuthenticatedPilot | null,
): string {
  const safeId = escapeHtml(d.id);
  const callsign = escapeHtml(d.pilotCallsign);
  const date = d.missionDate ? escapeHtml(d.missionDate) : '—';
  const mission = escapeHtml(
    resolveTargetLabel(d, missionsCache, declaredMissionsCache),
  );
  const campaign = escapeHtml(resolveCampaignLabel(d, declaredMissionsCache));
  const photoCount = String(d.photos.length);
  const visClass =
    d.visibility === 'public' ? 'bgallery-vis--public' : 'bgallery-vis--private';
  const visText = d.visibility === 'public' ? 'Público' : 'Privado';
  const updated = formatRelativeOrIso(d.updatedAt);
  const deleteBtn =
    user !== null && canDeleteDebriefing(user, d)
      ? `<button type="button" class="bgallery-delete-btn" data-bgallery-delete="${safeId}" title="Borrar debriefing" aria-label="Borrar debriefing">✕</button>`
      : '';

  return `
    <tr class="bgallery-row" data-debriefing-id="${safeId}" tabindex="0">
      <td class="bgallery-col-pilot">${callsign}</td>
      <td class="bgallery-col-date">${date}</td>
      <td class="bgallery-col-mission">${mission}</td>
      <td class="bgallery-col-campaign">${campaign}</td>
      <td class="bgallery-col-photos bgallery-cell-num">${photoCount}</td>
      <td class="bgallery-col-vis"><span class="bgallery-vis-badge ${visClass}">${visText}</span></td>
      <td class="bgallery-col-updated">${escapeHtml(updated)}</td>
      <td class="bgallery-col-actions">${deleteBtn}</td>
    </tr>
  `;
}

/**
 * Estado vacío. Con sesión invita a crear el primero; para anónimos
 * solo informa (sin enlace al editor).
 */
export function renderEmptyRow(user: AuthenticatedPilot | null): string {
  const cta =
    user !== null ? ` <a href="/debriefing.html">Crear el primero →</a>` : '';
  return `
    <tr>
      <td colspan="8" class="bgallery-empty">
        Aún no hay debriefings.${cta}
      </td>
    </tr>
  `;
}

export function renderErrorRow(message: string): string {
  return `
    <tr>
      <td colspan="8" class="bgallery-error">${escapeHtml(message)}</td>
    </tr>
  `;
}

function resolveTargetLabel(
  d: Debriefing,
  missionsCache: Map<string, BriefingMission>,
  declaredMissionsCache: Map<string, MissionSummary>,
): string {
  if (d.missionId) {
    const m = declaredMissionsCache.get(d.missionId);
    return m ? m.name : `(misión ${d.missionId.slice(0, 8)}…)`;
  }
  if (d.missionReportId) {
    const m = missionsCache.get(d.missionReportId);
    if (m) return m.title?.trim() || m.reportNumber;
    return `(reporte ${d.missionReportId.slice(0, 8)}…)`;
  }
  return '—';
}

/**
 * Campaña derivada de la misión (no se guarda directamente desde Fase
 * 8f.3). Devuelve "—" si la misión es suelta o no está asignada.
 */
function resolveCampaignLabel(
  d: Debriefing,
  declaredMissionsCache: Map<string, MissionSummary>,
): string {
  if (!d.missionId) return '—';
  const m = declaredMissionsCache.get(d.missionId);
  if (!m) return '—';
  return m.campaignName ?? '—';
}

function formatRelativeOrIso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `hoy ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) {
    return `ayer ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return d.toISOString().slice(0, 10);
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
